import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import type { ContractTemplate } from "@/lib/types/contracts";

/** Shape of a template row as returned by the esignatures.io templates API. */
interface ESignatureTemplateApiItem {
  id?: string;
  template_id?: string;
  title?: string;
  created_at?: string;
  updated_at?: string;
  status?: string;
  local_schema?: {
    description?: string;
    version?: string;
  } | null;
}

export function useContractTemplates() {
  const { session } = useUniversalAuth();

  return useQuery({
    queryKey: ["contract-templates"],
    queryFn: async (): Promise<ContractTemplate[]> => {
      try {
        const token = session?.access_token;
        if (!token) {
          throw new Error("Authentication required");
        }

        // Fetch templates from esignatures.io API
        const response = await fetch("/api/esignatures/templates", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch templates: ${response.status} ${response.statusText}`);
        }

        const apiResponse = await response.json();

        // Transform the API response to match our ContractTemplate interface
        if (apiResponse.data && Array.isArray(apiResponse.data)) {
          return (apiResponse.data as ESignatureTemplateApiItem[]).map((template) => {
            const id =
              template.id ||
              template.template_id ||
              `template-${Math.random().toString(36).substr(2, 9)}`;
            return {
              id,
              name: template.title || "Untitled Template",
              description:
                template.local_schema?.description ||
                `Template created on ${template.created_at ? new Date(template.created_at).toLocaleDateString() : "unknown date"}`,
              schema: null, // Will be loaded separately if needed
              templateId: template.template_id || template.id || id,
              version: template.local_schema?.version || "1.0",
              template_id: template.template_id,
              title: template.title,
              created_at: template.created_at,
              updated_at: template.updated_at,
              status: template.status,
              local_schema: template.local_schema
                ? {
                    id,
                    title: template.title ?? "",
                    description: template.local_schema.description ?? "",
                    version: template.local_schema.version ?? "1.0",
                    fields_count: 0,
                  }
                : undefined,
            };
          });
        }

        // Fallback to empty array if no data
        return [];
      } catch (error) {
        console.error("Error fetching contract templates:", error);

        // Throw error instead of returning fallback templates
        // This will trigger the error state in the UI
        throw new Error("SERVICE_UNAVAILABLE");
      }
    },
    enabled: !!session?.access_token, // Only run when authenticated
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2, // Retry failed requests twice
    retryDelay: 1000, // Wait 1 second between retries
  });
}
